/**
 *  版权所有 © 2026 东方闻道 All Rights Reserved
 *
 *  创建者: admin
 *  创建时间: 2026-09-20
 *  文件名: src/components/ppt-learning-plan-viewer/word-editor/office-editor-channel.methods.ts
 *  功能描述: EuroOffice iframe 主动调用方法
 */
const MESSAGE_BRIDGE_CHANNEL = 'eurooffice-editor';
const GOTO_BOOKMARK_METHOD = 'GOTO_BOOKMARK';
const SELECT_BOOKMARK_METHOD = 'SELECT_BOOKMARK';
const WAIT_DOCUMENT_SYNC_METHOD = 'WAIT_DOCUMENT_SYNC';
const FORCE_SAVE_METHOD = 'FORCE_SAVE';
// FORCE_SAVE 内部最多等待 15 秒的 DS 同步和 30 秒的业务同步，并为接口耗时预留空间。
const INVOKE_TIMEOUT = 60 * 1000;

let requestSequence = 0;

interface OfficeEditorChannelOptions {
  editorWindow: Window;
  editorOrigin: string;
}

interface InvokeMessage {
  channel: typeof MESSAGE_BRIDGE_CHANNEL;
  type: 'invoke';
  method: string;
  requestId?: string;
  params?: unknown;
}

interface InvokeResultMessage {
  channel: typeof MESSAGE_BRIDGE_CHANNEL;
  type: 'invokeResult';
  requestId: string;
  result?: unknown;
  error?: string;
}

interface BookmarkNameParams {
  name: string;
}

interface DocumentSyncResult {
  synced: true;
  modified: boolean;
}

interface DocumentForceSaveResult {
  fileId: string | null;
  syncState: 'COMPLETED';
}

/** 判断 iframe 消息是否为 EuroOffice 方法调用结果。 */
function isInvokeResultMessage(message: unknown): message is InvokeResultMessage {
  if (!message || typeof message !== 'object') return false;

  const invokeResultMessage = message as Partial<InvokeResultMessage>;
  return (
    invokeResultMessage.channel === MESSAGE_BRIDGE_CHANNEL &&
    invokeResultMessage.type === 'invokeResult' &&
    typeof invokeResultMessage.requestId === 'string'
  );
}

/** 向 EuroOffice iframe 发送不需要等待结果的方法调用。 */
function invokeOfficeEditor<TParams>(
  options: OfficeEditorChannelOptions,
  method: string,
  params?: TParams,
): void {
  const invokeMessage: InvokeMessage = {
    channel: MESSAGE_BRIDGE_CHANNEL,
    type: 'invoke',
    method,
    params,
  };
  options.editorWindow.postMessage(invokeMessage, options.editorOrigin);
}

/** 创建一次异步调用使用的唯一请求标识。 */
function createRequestId(): string {
  requestSequence += 1;
  return `${Date.now()}-${requestSequence}`;
}

/** 向 EuroOffice iframe 发送方法调用，并等待对应的调用结果。 */
function invokeOfficeEditorAsync<TResult, TParams = undefined>(
  options: OfficeEditorChannelOptions,
  method: string,
  params?: TParams,
): Promise<TResult> {
  const { editorWindow, editorOrigin } = options;
  const requestId = createRequestId();

  return new Promise<TResult>((resolve, reject) => {
    let timeoutId: number;

    /** 清理本次调用注册的消息监听和超时任务。 */
    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      window.clearTimeout(timeoutId);
    };

    /** 接收并处理当前请求对应的 iframe 返回结果。 */
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== editorWindow || event.origin !== editorOrigin) return;
      if (!isInvokeResultMessage(event.data) || event.data.requestId !== requestId) return;

      cleanup();
      if (event.data.error) {
        reject(new Error(event.data.error));
        return;
      }
      resolve(event.data.result as TResult);
    };

    window.addEventListener('message', handleMessage);
    timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error(`EuroOffice 方法调用超时: ${method}`));
    }, INVOKE_TIMEOUT);

    const invokeMessage: InvokeMessage = {
      channel: MESSAGE_BRIDGE_CHANNEL,
      type: 'invoke',
      method,
      requestId,
      params,
    };

    try {
      editorWindow.postMessage(invokeMessage, editorOrigin);
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
}

/** 通知 EuroOffice 编辑器跳转到指定书签。 */
function gotoOfficeEditorBookmark(options: OfficeEditorChannelOptions, bookmarkName: string): void {
  const params: BookmarkNameParams = { name: bookmarkName };
  invokeOfficeEditor(options, GOTO_BOOKMARK_METHOD, params);
}

/** 通知 EuroOffice 编辑器选中指定书签对应的内容。 */
function selectOfficeEditorBookmark(
  options: OfficeEditorChannelOptions,
  bookmarkName: string,
): void {
  const params: BookmarkNameParams = { name: bookmarkName };
  invokeOfficeEditor(options, SELECT_BOOKMARK_METHOD, params);
}

/** 等待 EuroOffice 将当前文档内容同步到业务服务器。 */
function waitOfficeEditorDocumentSync(
  options: OfficeEditorChannelOptions,
): Promise<DocumentSyncResult> {
  return invokeOfficeEditorAsync<DocumentSyncResult>(options, WAIT_DOCUMENT_SYNC_METHOD);
}

/** 请求 EuroOffice 强制保存当前文档，并等待业务服务器完成同步。 */
function forceSaveOfficeEditorDocument(
  options: OfficeEditorChannelOptions,
): Promise<DocumentForceSaveResult> {
  return invokeOfficeEditorAsync<DocumentForceSaveResult>(options, FORCE_SAVE_METHOD);
}

export {
  forceSaveOfficeEditorDocument,
  gotoOfficeEditorBookmark,
  MESSAGE_BRIDGE_CHANNEL,
  selectOfficeEditorBookmark,
  waitOfficeEditorDocumentSync,
};
export type {
  DocumentForceSaveResult,
  DocumentSyncResult,
  InvokeMessage,
  InvokeResultMessage,
  OfficeEditorChannelOptions,
};
